import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import TripsHeader from "./TripsHeader";
import TripsListWrapper from "./TripsListWrapper";
import { useGetTripsTrackQuery } from "../../services";
import moment from "moment";
import { NoData } from "../../../Shared";
import { QIErrorBoundary, QISpinner } from "../../../../components";

const Activities = ({
  deviceId,
  selectedRange,
  clientId,
  selectedVehiclePlate,
  type,
  setActivitiesTimeRange = () => {},
  setListCount = () => {},
}) => {
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState(); // To select the first row by default

  const [tracksData, setTracksData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const processedPagesRef = useRef(new Set());
  const loadMoreLockedRef = useRef(false);

  const rangeStart = useMemo(
    () => moment(selectedRange.startDate).valueOf(),
    [selectedRange.startDate]
  );
  const rangeEnd = useMemo(
    () => moment(selectedRange.endDate).valueOf(),
    [selectedRange.endDate]
  );

  // Context uniquely identifies the current data set. When it changes (device,
  // range, plate, or batch size), the accumulated rows are reset so pages from
  // a previous selection are never mixed in.
  const dataContext = `${type}:${deviceId || ""}:${selectedVehiclePlate || ""}:${rangeStart}:${rangeEnd}:${perPage}`;

  useEffect(() => {
    setTracksData([]);
    setTotalCount(0);
    setPage(1);
    setSelectedId(undefined);
    processedPagesRef.current.clear();
    loadMoreLockedRef.current = false;
  }, [dataContext]);

  const {
    data: response,
    error,
    isFetching,
    isSuccess,
  } = useGetTripsTrackQuery(
    deviceId && {
      deviceId: deviceId,
      startTime: rangeStart,
      endTime: rangeEnd,
      clientId: clientId,
      page: page - 1,
      per_page: perPage,
      plate_number: [selectedVehiclePlate],
      source_id: [deviceId],
      type: type,
      search: false,
    }
  );

  //To set the total count of data in the list
  useEffect(() => {
    setListCount(response?.total_count);
  }, [response?.total_count]);

  // Get end time if present for past trip or return current time for in progress trip
  const getEndTime = (endTime) => {
    if (endTime !== null && endTime !== "-") {
      return moment(endTime);
    } else {
      return moment();
    }
  };

  // Accumulate pages instead of replacing. Each page is processed once, guarded
  // by processedPagesRef so React re-renders cannot duplicate rows.
  useEffect(() => {
    if (!response || !isSuccess || isFetching || !deviceId) return;

    loadMoreLockedRef.current = false;
    setTotalCount(response.total_count || 0);

    const processedPageKey = `${dataContext}:${page}`;
    if (processedPagesRef.current.has(processedPageKey)) return;
    processedPagesRef.current.add(processedPageKey);

    if (response?.activities?.length) {
      setTracksData((previousRows) => [...previousRows, ...response.activities]);
    }
  }, [response, isSuccess, isFetching, deviceId, dataContext, page]);

  // Select the first row and align the map time range once the first page loads.
  useEffect(() => {
    if (page === 1 && response?.activities?.length > 0) {
      setActivitiesTimeRange({
        start_time: moment(response?.activities[0]?.data?.start_time),
        end_time: getEndTime(response?.activities[0]?.data?.end_time),
      });
      setSelectedId(0);
    }
  }, [response?.activities, page]);

  useEffect(() => {
    if (error) {
      loadMoreLockedRef.current = false;
    }
  }, [error]);

  const hasMore = tracksData.length < totalCount;

  const handleLoadMore = useCallback(() => {
    if (loadMoreLockedRef.current || isFetching || !hasMore) return;
    loadMoreLockedRef.current = true;
    setPage((currentPage) => currentPage + 1);
  }, [hasMore, isFetching]);

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage);
  }, []);

  const message = {
    header: error?.data?.message,
  };

  return (
    <>
      <TripsHeader />
      {error && <QIErrorBoundary message={message} />}
      {isFetching && tracksData.length === 0 && (
        <div className="no-data">
          <QISpinner size="50px" />
        </div>
      )}
      {tracksData?.length ? (
        <TripsListWrapper
          tracksData={tracksData || []}
          batchSize={perPage}
          onBatchSizeChange={handlePerPageChange}
          hasMore={hasMore}
          isLoadingMore={isFetching}
          onLoadMore={handleLoadMore}
          setActivitiesTimeRange={setActivitiesTimeRange}
          selectedId={selectedId}
        />
      ) : (
        isSuccess && !error && !isFetching && <NoData />
      )}
    </>
  );
};

export default Activities;
